// client/src/lib/services/sync-manager.ts
// ============================================
// GESTOR CENTRALIZADO DE SINCRONIZACIÓN OFFLINE
// ============================================
// Propósito: Manejar la cola de operaciones pendientes y sincronizarlas
// cuando se recupera la conexión.
//
// Responsabilidades:
// 1. Encolar operaciones (ventas, inventario, turnos)
// 2. Detectar recuperación de conexión
// 3. Sincronizar en lote con reintentos
// 4. Resolver conflictos
// 5. Notificar estado al usuario
//
// Arquitectura:
// - sync-manager.ts (este archivo): lógica central
// - indexed-db.ts: persistencia de cola
// - offline-context.tsx: detección de conectividad
// - usePOSOffline.ts: integración con POS

import { indexedDBService, type PendingOperation } from "../db/indexed-db";
import { AuthService } from "./auth-offline";

// ============================================
// CONFIGURACIÓN
// ============================================
const CONFIG = {
  MAX_RETRIES: 3,
  MAX_DEPENDENCY_RETRIES: 5,
  RETRY_DELAY_MS: 1000,
  BATCH_SIZE: 3,
  SYNC_INTERVAL_MS: 10000,
  STAGGER_DELAY_MS: 800,
  BATCH_PAUSE_MS: 3000,
  INITIAL_SYNC_DELAY_MS: 1500,
};

// ============================================
// TIPOS
// ============================================
export type SyncStatus =
  | "idle"
  | "syncing"
  | "completed"
  | "failed"
  | "partial"
  | "skipped";

export interface SyncResult {
  status: SyncStatus;
  syncedCount: number;
  failedCount: number;
  errors: SyncError[];
}

export interface SyncError {
  operationId: string;
  type: string;
  message: string;
  timestamp: string;
}

export interface SyncStats {
  pending: number;
  completed: number;
  failed: number;
  pendingApproval: number;
  lastSyncAt: string | null;
}

// ============================================
// TIPO PARA LISTENERS DE EVENTOS
// ============================================
type SyncEventListener = (event: SyncEvent) => void;

interface SyncEvent {
  type:
    | "sync:start"
    | "sync:progress"
    | "sync:complete"
    | "sync:error"
    | "sync:offline"
    | "sync:online"
    | "sync:pending-auth"
    | "sync:shift-closed";
  data?: any;
}

// ============================================
// CLASE PRINCIPAL
// ============================================
class SyncManager {
  private listeners: SyncEventListener[] = [];
  private syncInProgress: boolean = false;
  private intervalId: number | null = null;

  // ============================================
  // SUSCRIPCIÓN A EVENTOS
  // ============================================

  // {Permite que componentes escuchen eventos de sincronización}
  on(listener: SyncEventListener): () => void {
    this.listeners.push(listener);
    // {Retornar función para desuscribirse}
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  // {Emitir evento a todos los listeners}
  private emit(event: SyncEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (e) {
        console.error("❌ [SyncManager] Error en listener:", e);
      }
    });
  }

  // ============================================
  // INICIALIZACIÓN
  // ============================================

  // {Iniciar monitoreo de conexión para auto-sync}
  startAutoSync(): void {
    console.log("🔄 [SyncManager] Iniciando auto-sync...");

    // {Verificar inmediatamente al iniciar}
    this.checkAndSync();

    // {Verificar conexión periódicamente}
    this.intervalId = window.setInterval(() => {
      this.checkAndSync();
    }, CONFIG.SYNC_INTERVAL_MS);

    // {También escuchar evento online del navegador}
    window.addEventListener("online", () => {
      console.log("🌐 [SyncManager] Conexión recuperada");
      this.emit({ type: "sync:online" });
      // {Esperar para estabilizar conexión antes de sincronizar}
      setTimeout(() => {
        this.checkAndSync();
      }, CONFIG.INITIAL_SYNC_DELAY_MS);
    });

    window.addEventListener("offline", () => {
      console.log("📴 [SyncManager] Sin conexión");
      this.emit({ type: "sync:offline" });
    });
  }

  // {Detener auto-sync}
  stopAutoSync(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log("⏹️ [SyncManager] Auto-sync detenido");
    }
  }

  // ============================================
  // ENCOLAR OPERACIONES
  // ============================================

  // {Agregar una operación a la cola para sincronizar después}
  async enqueue(operation: {
    type: "create" | "update" | "delete";
    entityType: "inventory" | "product" | "sale" | "customer" | "shift";
    data: any;
    shopId?: string;
    priority?: "high" | "normal" | "low";
  }): Promise<string> {
    const pendingOp: PendingOperation = {
      id: crypto.randomUUID(),
      type: operation.type,
      entityType: operation.entityType,
      data: operation.data,
      timestamp: new Date().toISOString(),
      retryCount: 0,
      shopId: operation.shopId,
      priority: operation.priority || "normal",
      maxRetries: CONFIG.MAX_RETRIES,
      originalCreatedAt: new Date().toISOString(),
      status: "pending",
    };

    await indexedDBService.addPendingOperation(pendingOp);
    console.log(
      `📥 [SyncManager] Operación encolada: ${operation.entityType}/${operation.type} (${pendingOp.id})`,
    );

    return pendingOp.id;
  }

  // ============================================
  // VERIFICAR Y SINCRONIZAR
  // ============================================

  // {Verificar conexión real al servidor y sincronizar si es posible}
  async checkAndSync(): Promise<SyncResult | null> {
    if (this.syncInProgress) {
      console.log("⏳ [SyncManager] Sincronización ya en progreso, esperando...");
      return null;
    }

    // {Verificar alcance real del servidor API}
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch("/api/health", {
        method: "HEAD",
        signal: controller.signal,
        cache: "no-cache",
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        console.log("📴 [SyncManager] Servidor no disponible (status)", response.status);
        return null;
      }
    } catch {
      console.log("📴 [SyncManager] Sin conexión al servidor");
      return null;
    }

    // {Verificar que haya sesión válida}
    const session = await AuthService.getSession();
    if (!session) {
      console.log("🔒 [SyncManager] No hay sesión activa, no se puede sincronizar");
      return null;
    }

    // {Verificar si hay turno offline pendiente de autorización local}
    if (session.shopId) {
      const pendingAuth = await indexedDBService.getPendingAuthorizationOfflineShift(session.shopId);
      if (pendingAuth) {
        console.log(
          "🔐 [SyncManager] Turno pendiente de autorización detectado, emitiendo evento y bloqueando sync",
        );
        this.emit({
          type: "sync:pending-auth",
          data: {
            shiftId: pendingAuth.id,
            countedCash: pendingAuth.countedCash,
            expectedCash: pendingAuth.expectedCash,
            difference: pendingAuth.difference,
            denominations: pendingAuth.denominationBreakdown || {},
            notes: pendingAuth.notes || "",
            requiresAuthorization: true,
            shopId: session.shopId,
          },
        });
        return null;
      }
    }

    return this.syncAll(session.token);
  }

  // ============================================
  // SINCRONIZAR TODO
  // ============================================

  // {Sincronizar todas las operaciones pendientes}
  async syncAll(token: string): Promise<SyncResult> {
    if (this.syncInProgress) {
      console.log("⏳ [SyncManager] syncAll ya en progreso, saltando...");
      return { status: "skipped", syncedCount: 0, failedCount: 0, errors: [] };
    }
    this.syncInProgress = true;
    this.emit({ type: "sync:start" });

    const errors: SyncError[] = [];
    let syncedCount = 0;
    let failedCount = 0;

    try {
      // {Obtener operaciones pendientes ordenadas por prioridad}
      const pendingOps =
        await indexedDBService.getPrioritizedPendingOperations();

      if (pendingOps.length === 0) {
        console.log("✅ [SyncManager] No hay operaciones pendientes");
        this.syncInProgress = false;
        this.emit({ type: "sync:complete", data: { status: "completed", syncedCount: 0, failedCount: 0, errors: [] } });
        return {
          status: "completed",
          syncedCount: 0,
          failedCount: 0,
          errors: [],
        };
      }

      console.log(
        `🔄 [SyncManager] Sincronizando ${pendingOps.length} operaciones...`,
      );

      // {Procesar en lotes escalonados}
      for (let i = 0; i < pendingOps.length; i += CONFIG.BATCH_SIZE) {
        const batch = pendingOps.slice(i, i + CONFIG.BATCH_SIZE);

        for (const op of batch) {
          const result = await this.syncOperation(op, token);

          if (result === true) {
            syncedCount++;
            await indexedDBService.removePendingOperation(op.id);
          } else if (result === "dependency") {
            // Dependencia no resuelta (ej: shift/update sin realId), no contar como fallo
            // Pero limitar reintentos para evitar loop infinito
            const depRetryCount = (op as any).dependencyRetryCount || 0;
            const newDepRetry = depRetryCount + 1;
            if (newDepRetry >= CONFIG.MAX_DEPENDENCY_RETRIES) {
              await indexedDBService.updateOperationStatus(op.id, "failed");
              errors.push({
                operationId: op.id,
                type: op.entityType,
                message: `Dependencia sin resolver tras ${newDepRetry} intentos`,
                timestamp: new Date().toISOString(),
              });
              console.warn(
                `⚠️ [SyncManager] Operación marcada como fallida por dependencia no resuelta: ${op.id}`,
              );
            } else {
              await indexedDBService.updatePendingOperation(op.id, {
                dependencyRetryCount: newDepRetry,
              } as any);
            }
          } else {
            failedCount++;

            const newRetryCount = op.retryCount + 1;
            if (newRetryCount >= (op.maxRetries || CONFIG.MAX_RETRIES)) {
              await indexedDBService.updateOperationStatus(op.id, "failed");
              errors.push({
                operationId: op.id,
                type: op.entityType,
                message: `Falló después de ${newRetryCount} intentos`,
                timestamp: new Date().toISOString(),
              });
            } else {
              await indexedDBService.updatePendingOperation(op.id, {
                retryCount: newRetryCount,
                lastError: "Reintentando...",
              });
            }
          }

          // {Pausa escalonada entre operaciones individuales}
          await new Promise((resolve) => setTimeout(resolve, CONFIG.STAGGER_DELAY_MS));
        }

        // {Emitir progreso después de cada lote}
        this.emit({
          type: "sync:progress",
          data: {
            current: Math.min(i + batch.length, pendingOps.length),
            total: pendingOps.length,
            syncedCount,
            failedCount,
          },
        });

        // {Pausa entre lotes para no saturar el servidor}
        if (i + CONFIG.BATCH_SIZE < pendingOps.length) {
          await new Promise((resolve) => setTimeout(resolve, CONFIG.BATCH_PAUSE_MS));
        }
      }
    } catch (error: any) {
      console.error("❌ [SyncManager] Error general:", error);
    }

    this.syncInProgress = false;

    const result: SyncResult = {
      status:
        failedCount === 0
          ? "completed"
          : syncedCount > 0
            ? "partial"
            : "failed",
      syncedCount,
      failedCount,
      errors,
    };

    console.log(
      `✅ [SyncManager] Sincronización completada: ${syncedCount} ok, ${failedCount} fallos`,
    );
    this.emit({ type: "sync:complete", data: result });

    return result;
  }

  // ============================================
  // SINCRONIZAR UNA OPERACIÓN
  // ============================================

  // {Enviar una operación individual al backend}
  // Retorna: true=éxito, false=fallo real, "dependency"=dependencia no lista (no cuenta como fallo)
  private async syncOperation(
    op: PendingOperation,
    token: string,
  ): Promise<boolean | "dependency"> {
    let url: string = "";
    let body: any = null;

    console.log(
      `🔍 [SyncManager] syncOperation entrada: entityType=${op.entityType}, type=${op.type}, id=${op.id}, status=${op.status}, retryCount=${op.retryCount}`,
    );
    console.log(
      `🔍 [SyncManager] op.data keys: ${Object.keys(op.data || {}).join(", ")}`,
    );

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000); // 10 segundos timeout

      let method: string;

      // {Determinar endpoint según tipo de operación}
      switch (op.entityType) {
        case "sale":
          console.log(`📦 [SyncManager] switch -> case "sale"`);
          // Si la venta tiene un shiftId offline, buscar el ID real
          if (op.data.shiftId && op.data.shiftId.startsWith("offline_shift_")) {
            const offlineShift = await indexedDBService.getOfflineShiftById(
              op.data.shiftId,
            );
            if (
              offlineShift?.realId &&
              !offlineShift.realId.startsWith("offline_")
            ) {
              op.data.shiftId = offlineShift.realId;
              console.log(
                `🔄 [SyncManager] shiftId mapeado: ${op.data.shiftId} -> ${offlineShift.realId}`,
              );
            }
          }
          url = "/api/sales";
          method = "POST";
          body = op.data;
          break;
        case "inventory":
          url = `/api/inventory/${op.data.id || ""}`;
          method =
            op.type === "create"
              ? "POST"
              : op.type === "update"
                ? "PATCH"
                : "DELETE";
          body = op.data;
          break;
        case "product":
          url = `/api/products${op.data.id ? `/${op.data.id}` : ""}`;
          method =
            op.type === "create"
              ? "POST"
              : op.type === "update"
                ? "PATCH"
                : "DELETE";
          body = op.data;
          break;
        case "shift":
          console.log(`🔄 [SyncManager] switch -> case "shift", type=${op.type}`);
          if (op.type === "create") {
            console.log(`📂 [SyncManager] shift/create - preparando apertura`);
            // Si el employeeId es sintético (shop_/user_), convertirlo a virtual_owner_
            let effectiveEmployeeId = op.data.employeeId;
            if (effectiveEmployeeId && (effectiveEmployeeId.startsWith("shop_") || effectiveEmployeeId.startsWith("user_"))) {
              effectiveEmployeeId = `virtual_owner_${op.data.shopId}`;
              console.log(`🔄 [SyncManager] employeeId mapeado: ${op.data.employeeId} -> ${effectiveEmployeeId}`);
            }
            console.log(`📂 [SyncManager] shift/create body:`, {
              shopId: op.data.shopId,
              cashRegisterId: op.data.cashRegisterId,
              employeeId: effectiveEmployeeId,
              initialCash: op.data.initialCash,
              openedBy: op.data.openedBy,
            });
            // Abrir turno en backend
            url = "/api/shifts/open";
            method = "POST";
            body = {
              shopId: op.data.shopId,
              cashRegisterId: op.data.cashRegisterId,
              employeeId: effectiveEmployeeId,
              initialCash: op.data.initialCash,
              openedBy: op.data.openedBy,
            };
          } else if (op.type === "update" && op.data?.countedCash) {
            console.log(`📝 [SyncManager] shift/update - preparando cierre`);
            console.log(`📝 [SyncManager] shift/update shiftId original: ${op.data.shiftId}`);
            // Si el shiftId es offline, buscar el ID real
            let realShiftId = op.data.shiftId;
            if (realShiftId && realShiftId.startsWith("offline_shift_")) {
              const offlineShift =
                await indexedDBService.getOfflineShiftById(realShiftId);
              if (offlineShift?.realId) {
                realShiftId = offlineShift.realId;
                console.log(
                  `🔄 [SyncManager] shiftId de cierre mapeado: ${op.data.shiftId} -> ${realShiftId}`,
                );
              }
            }
            console.log(`📝 [SyncManager] shift/update realShiftId después de mapeo: ${realShiftId}`);
            // Si no tenemos ID real todavía, reintentar después
            if (!realShiftId || realShiftId.startsWith("offline_shift_")) {
              console.warn(
                `⚠️ [SyncManager] Turno aún no sincronizado, reintentando después: ${realShiftId}`,
              );
              return "dependency"; // Reintentar en el próximo ciclo sin contar como fallo
            }
            // Cerrar turno en backend
            url = `/api/shifts/${realShiftId}/close`;
            method = "POST";
            body = {
              countedCash: op.data.countedCash,
              notes: op.data.notes,
              closedBy: op.data.openedBy || "Usuario Offline",
              denominationBreakdown: op.data.denominationBreakdown,
              closedOffline: true,
              offlineClosedAt: op.data.closedAt,
              requiresAuthorization: op.data.requiresAuthorization || false,
              expectedCash: op.data.expectedCash,
              difference: op.data.difference,
              authorizationCode: op.data.authorizationCode,
            };
          } else {
            console.warn(
              `⚠️ [SyncManager] Tipo de operación shift desconocido: type=${op.type}, countedCash=${op.data?.countedCash}`,
            );
            return false;
          }
          break;
        case "customer":
          url = "/api/customers";
          method = "POST";
          body = op.data;
          break;
        default:
          console.warn(
            `⚠️ [SyncManager] Tipo de entidad DESCONOCIDO: "${op.entityType}" (no entró en ningún case)`,
          );
          return false;
      }

      console.log(`📡 [SyncManager] Enviando ${method} ${url}`);
      console.log(`📡 [SyncManager] Body:`, JSON.stringify(body, null, 2).substring(0, 500));

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: method !== "DELETE" ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      console.log(`📡 [SyncManager] Respuesta: status=${response.status}, ok=${response.ok}, url=${url}`);

      if (response.ok) {
        console.log(
          `✅ [SyncManager] Operación sincronizada: ${op.entityType} (${op.id})`,
        );

        // Si es venta, marcar como sincronizada en IndexedDB
        if (op.entityType === "sale") {
          try {
            await indexedDBService.markSaleAsSynced(op.data.id);
          } catch (e) {
            console.warn(
              "⚠️ [SyncManager] Error marcando venta como sincronizada:",
              e,
            );
          }
        }

        // Si es apertura de turno, actualizar el ID real en IndexedDB
        if (op.entityType === "shift" && op.type === "create") {
          try {
            const result = await response.json();
            const realShiftId = result.shift?.id;
            if (realShiftId && op.data.shiftId) {
              const offlineShift = await indexedDBService.getOfflineShiftById(
                op.data.shiftId,
              );
              if (offlineShift) {
                offlineShift.realId = realShiftId;
                await indexedDBService.saveOfflineShift(offlineShift);
                await indexedDBService.updateSalesShiftId(
                  op.data.shiftId,
                  realShiftId,
                );
                // {FIX C: Marcar como sincronizado para que getActiveOfflineShift no lo devuelva como turno activo}
                await indexedDBService.markShiftAsSynced(op.data.shiftId);
                console.log(
                  `🔄 [SyncManager] Turno offline mapeado: ${op.data.shiftId} -> ${realShiftId}`,
                );
              }
              // Actualizar también el shiftId en la operación pendiente shift/update si existe
              const allPending = await indexedDBService.getPrioritizedPendingOperations();
              const pendingUpdate = allPending.find(
                (p) =>
                  p.entityType === "shift" &&
                  p.type === "update" &&
                  p.data.shiftId === op.data.shiftId,
              );
              if (pendingUpdate) {
                pendingUpdate.data.shiftId = realShiftId;
                await indexedDBService.updatePendingOperation(pendingUpdate.id, {
                  data: pendingUpdate.data,
                });
                console.log(
                  `✅ [SyncManager] pending shift/update actualizado con realShiftId: ${realShiftId}`,
                );
              }
            }
          } catch (e) {
            console.warn(
              "⚠️ [SyncManager] Error procesando respuesta de turno:",
              e,
            );
          }
        }

        // Si es cierre de turno, eliminar turno de IndexedDB
        if (op.entityType === "shift" && op.type === "update") {
          // Emitir evento para que la UI descargue el PDF del acta de cierre
          try {
            const closedShift = await indexedDBService.getOfflineShiftById(op.data.shiftId);
            const closedShiftId = closedShift?.realId || op.data.shiftId;
            this.emit({ type: "sync:shift-closed", data: { shiftId: closedShiftId } });
          } catch (e) {
            console.warn("⚠️ [SyncManager] Error al emitir shift-closed:", e);
          }
          try {
            await indexedDBService.deleteOfflineShift(op.data.shiftId);
            console.log(
              `🗑️ [SyncManager] Turno offline eliminado tras cierre: ${op.data.shiftId}`,
            );
            // También eliminar el backup del turno real guardado durante shift/create
            const shiftBackup = await indexedDBService.getOfflineShiftById(op.data.shiftId);
            const realShiftId = shiftBackup?.realId || op.data.shiftId;
            if (realShiftId && realShiftId !== op.data.shiftId) {
              await indexedDBService.deleteOfflineShift(realShiftId);
              console.log(
                `🗑️ [SyncManager] Backup de turno eliminado: ${realShiftId}`,
              );
            }
          } catch (e) {
            console.warn("⚠️ [SyncManager] Error eliminando turno offline:", e);
          }
        }

        return true;
      }

      const errorBody = await response.text();
      console.error(
        `❌ [SyncManager] Error ${response.status} en ${op.entityType}/${op.type}:`,
        errorBody,
        "\nBody enviado:",
        JSON.stringify(body, null, 2),
      );

      // ============ MANEJO ESPECIAL: TURNO ACTIVO EXISTENTE ============
      if (
        response.status === 400 &&
        op.entityType === "shift" &&
        op.type === "create" &&
        errorBody.includes("Ya existe un turno activo")
      ) {
        console.log(`📄 [SyncManager] errorBody:`, errorBody);
        try {
          console.log(
            `🔄 [SyncManager] Detectado turno activo existente, recuperándolo...`,
          );
          const activeResp = await fetch(
            `/api/shops/${op.data.shopId}/active-shift`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "x-shop-id": op.data.shopId,
              },
            },
          );
          console.log(
            `📡 [SyncManager] active-shift respuesta: status=${activeResp.status}, ok=${activeResp.ok}`,
          );
          if (activeResp.ok) {
            const activeData = await activeResp.json();
            const realShiftId = activeData.shift?.id;
            console.log(
              `🔄 [SyncManager] active-shift data:`, JSON.stringify(activeData),
            );
            if (realShiftId && op.data.shiftId) {
              console.log(
                `🔄 [SyncManager] Mapeando turno offline ${op.data.shiftId} -> ${realShiftId}`,
              );
              const offlineShift = await indexedDBService.getOfflineShiftById(
                op.data.shiftId,
              );
              if (offlineShift) {
                offlineShift.realId = realShiftId;
                await indexedDBService.saveOfflineShift(offlineShift);
                // {FIX C: Marcar como sincronizado para que getActiveOfflineShift no lo devuelva}
                await indexedDBService.markShiftAsSynced(op.data.shiftId);
                console.log(
                  `✅ [SyncManager] offlineShift.realId actualizado: ${realShiftId}`,
                );
              } else {
                console.warn(
                  `⚠️ [SyncManager] No se encontró offlineShift en IndexedDB: ${op.data.shiftId}`,
                );
              }
              await indexedDBService.updateSalesShiftId(
                op.data.shiftId,
                realShiftId,
              );
              // Actualizar también el shiftId en la operación pendiente shift/update si existe
              const allPending = await indexedDBService.getPrioritizedPendingOperations();
              const pendingUpdate = allPending.find(
                (p) =>
                  p.entityType === "shift" &&
                  p.type === "update" &&
                  p.data.shiftId === op.data.shiftId,
              );
              if (pendingUpdate) {
                pendingUpdate.data.shiftId = realShiftId;
                await indexedDBService.updatePendingOperation(pendingUpdate.id, {
                  data: pendingUpdate.data,
                });
                console.log(
                  `✅ [SyncManager] pending shift/update actualizado con realShiftId: ${realShiftId}`,
                );
              }
              console.log(
                `✅ [SyncManager] Turno offline mapeado a turno activo existente: ${realShiftId}`,
              );
              return true;
            } else {
              console.warn(
                `⚠️ [SyncManager] active-shift response sin shift.id o sin op.data.shiftId: realShiftId=${realShiftId}, op.data.shiftId=${op.data.shiftId}, activeData=${JSON.stringify(activeData)}`,
              );
            }
          } else {
            const activeErrorBody = await activeResp.text();
            console.warn(
              `⚠️ [SyncManager] active-shift fetch falló: status=${activeResp.status}, body=${activeErrorBody}`,
            );
          }
          console.warn(
            `⚠️ [SyncManager] No se pudo recuperar el turno activo existente`,
          );
        } catch (e) {
          console.warn(
            "⚠️ [SyncManager] Error al recuperar turno activo existente:",
            e,
          );
        }
      }
      // ============ FIN MANEJO ESPECIAL ============

      // {Guardar el error en la operación para debug}
      await indexedDBService.updatePendingOperation(op.id, {
        lastError: `HTTP ${response.status}: ${errorBody}`,
      });
      return false;
    } catch (error: any) {
      console.error(
        `❌ [SyncManager] Error sincronizando ${op.entityType}/${op.type}:`,
        error.message,
        "\nStack:",
        error.stack,
        "\nURL:",
        url,
        "\nBody:",
        JSON.stringify(body, null, 2),
      );
      return false;
    }
  }

  // ============================================
  // ESTADÍSTICAS
  // ============================================

  // {Obtener estadísticas de la cola de sincronización}
  async getStats(): Promise<SyncStats> {
    const ops = await indexedDBService.getPendingOperations();
    const lastSync = await indexedDBService.getSyncTimestamp("lastSync");

    return {
      pending: ops.filter(
        (o) => o.status === "pending" || o.status === "syncing",
      ).length,
      completed: ops.filter((o) => o.status === "completed").length,
      failed: ops.filter((o) => o.status === "failed").length,
      pendingApproval: ops.filter((o) => o.status === "pending_approval")
        .length,
      lastSyncAt: lastSync ? new Date(lastSync).toISOString() : null,
    };
  }

  // {Limpiar operaciones completadas antiguas}
  async cleanup(): Promise<void> {
    await indexedDBService.cleanupCompletedOperations(24); // Mantener fallidas por 24h
    console.log("🧹 [SyncManager] Cola limpiada");
  }
}

// ============================================
// EXPORTAR INSTANCIA ÚNICA (SINGLETON)
// ============================================
export const syncManager = new SyncManager();

// {Exponer syncManager en window para debugging}
if (typeof window !== "undefined") {
  (window as any).__syncManager = syncManager;
  console.log("🔧 [SyncManager] Expuesto en window.__syncManager");
}
